import PiadaOneApi from './PiadaOneApi';
import { plainToClass } from 'class-transformer';
import Basket from '../classes/Basket';
import type Order from '../classes/Order';
import Product from '../classes/Product';
import OrderDetails from '../classes/OrderDetails';
import { useUserStore } from '../stores/user';
import LoyaltyScheme from '../classes/LoyaltyScheme';
import type Reward from '../types/Reward';
import type OrderProduct from '../types/OrderProduct';
import type LoyaltyReward from '../types/LoyaltyReward';
import type AvailableTime from '../types/AvailableTime';
import type BillingScheme from '../types/BillingScheme';
import type ChoiceCustomFieldInterface from '@/types/ChoiceCustomField';
import type { DateTime } from "luxon";
import axios from 'axios';


export default {
  createBasket(vendorid: number): Promise<any> {
    const body = JSON.stringify({
      "vendorid": vendorid,
    })

    return new Promise((resolve, reject) => {
      PiadaOneApi.post(
        '/olo/baskets/create',
        body,
        {
          // No need for custom headers here
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data) {
          basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })
          
          resolve(basket);
        } else {
          reject(new Error('ERROR'));
        }
      });
    });
  },
  getBasket(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      PiadaOneApi.get(
        '/olo/baskets/'+id,
        {
          // No need for custom headers here
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data) {
          basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })
          
          resolve(basket);
        } else {        
          reject(new Error('ERROR'));
        }
      }).catch((error: Error) => {
          reject(error);
      });
    });
  },
  setMypiadaBasket(id?: string, location?: number): Promise<any> {
    return new Promise((resolve, reject) => {
      let url = '/users/set_basket';

      let body = {
        clear_basket: true
      } as any;

      if (id && location) {
        body = {
          "basket_id": id,
          "restaurant_id": location
        }
      }

      JSON.stringify(body);

      PiadaOneApi.post(
        url,
        body,
        {
          // No need for custom headers here
        },
      ).then((response) => {
        resolve('');
      }).catch((error: Error) => {
        reject(error);
      });
    });
  },
  reorder(id: string): Promise<any> {
    const body = JSON.stringify({
      "id": id,
    })

    return new Promise((resolve, reject) => {
      PiadaOneApi.post(
        '/olo/baskets/createfromorder',
        body,
        {
          // No need for custom headers here
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data) {
          basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })

          resolve(basket);
        } else {
          reject(new Error('ERROR'));
        }
      }).catch((error: Error) => {
        reject(error);
      });
    });
  },
  editOrder(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      PiadaOneApi.post(
        '/olo/orders/' + id + '/edit',
        {
          // No need for custom headers here
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data) {
          basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })

          resolve(basket);
        } else {
          reject(new Error('ERROR'));
        }
      }).catch((error: Error) => {
        reject(error);
      });
    });
  },
  cancelEditOrder(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      PiadaOneApi.delete(
        '/baskets/' + id + '/edit',
        {
          // No need for custom headers here
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data) {
          resolve(null);
        } else {
          reject(new Error('Edit could not be canceled.'));
        }
      }).catch((error: Error) => {
        reject(error);
      });
    });
  },

  //--------------------------------------------------------------------------------------
  //
  // Basket Products
  //
  // -------------------------------------------------------------------------------------
  addProduct(bid: string, pid: string, qty: number, specialInstructions: string, choices: number[], choicecustomfields: ChoiceCustomFieldInterface[]): Promise<any> {
    return new Promise((resolve, reject) => {

      const options_string = choices.length > 0 ? choices.toString() : ''

      const body = JSON.stringify({
        "productid": pid,
        "quantity": qty,
        "specialinstructions": specialInstructions,
        "options": options_string,
        "choicecustomfields": choicecustomfields
      })

      PiadaOneApi.post(
        '/olo/baskets/'+bid+'/products',
        body,
        {
          // No need for custom headers here
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data && response.data.earliestreadytime) {
          // We assume if there's an ID on the response data, we got back a basket
          basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })
            
          resolve(basket);
        } else if (response && response.data && response.data.message) {
          reject(new Error(response.data.message));
        } else {
          reject(new Error('Item could not be added. Please make sure all required selections have been made and try again.'));
        }
      }).catch((error: Error) => {
        if (axios.isAxiosError(error)) {
          // Do something with this error...
          if (error.response && error.response.data && error.response.data.message) {
            reject(new Error(error.response.data.message));
          } else if (error.response && error.response.data && typeof(error.response.data) == "string") {
            reject(new Error(error.response.data))
          } else {
            reject(error);
          }
        } else {
          reject(error);
        }
      });
    });
  },
  addMultipleProducts(bid: string, products: OrderProduct[]): Promise<any> {
    return new Promise((resolve, reject) => {

      const body = JSON.stringify({products: products});

      PiadaOneApi.post(
        '/baskets/'+bid+'/multiple_products',
        body,
        {
          // No need for custom headers here
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data && response.data.earliestreadytime) {
          // We assume if there's an ID on the response data, we got back a basket
          basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })
            
          resolve(basket);
        } else if (response && response.data && response.data.message) {
          reject(new Error(response.data.message));
        } else {
          reject(new Error('Items could not be added. Please make sure all required selections have been made and try again.'));
        }
      }).catch((error: Error) => {
        if (axios.isAxiosError(error)) {
          // Do something with this error...
          if (error.response && error.response.data && error.response.data.message) {
            reject(new Error(error.response.data.message));
          } else if (error.response && error.response.data && typeof(error.response.data) == "string") {
            reject(new Error(error.response.data))
          } else {
            reject(error);
          }
        } else {
          reject(error);
        }
      });
    });
  },
  removeProduct(bid: string, bpid: number): Promise<any> {
    return new Promise((resolve, reject) => {
      PiadaOneApi.delete(
        '/olo/baskets/'+bid+'/products/'+bpid.toString(),
        {
          // No need for custom headers here
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data) {
          basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })
          
          resolve(basket);
        } else {
          reject(new Error('ERROR'));
        }
      });
    });
  },
  updateProduct(bid: string, pid: string, bpid: string, qty: number, specialInstructions: string, choices: number[]): Promise<any> {
    return new Promise((resolve, reject) => {

      const options_string = choices.length > 0 ? choices.toString() : ''

      const body = JSON.stringify({
        "productid": pid,
        "quantity": qty,
        "specialinstructions": specialInstructions,
        "options": options_string
      })

      PiadaOneApi.put(
        '/olo/baskets/'+bid+'/products/'+bpid,
        body,
        {
          // No need for custom headers here
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data) {
          if (response.data.message) {
            reject(response.data.message);
          } else {
            basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })
            
            resolve(basket);
          }
        } else {
          reject(new Error('ERROR'));
        }
      }).catch((error: Error) => {
          reject(error);
      });
    });
  },
  fetchUpsells(bid: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = '/baskets/'+bid+'/upsells'
      PiadaOneApi.get(
        url,
        {
          headers: {
            'X-User-Verify': 'url'
          }
        },
      ).then((response) => {
        let upsells = null as (Product[] | null);

        if (response.status === 200 && response.data && response.data.upsells) {
          const jsonObjs = response.data.upsells;

          upsells = jsonObjs.map(
            (prod: Object) => plainToClass(Product, (prod as object), { excludeExtraneousValues: true }),
          );

          resolve(upsells);
        } else {        
          reject(new Error('ERROR'));
        }
      }).catch((error: Error) => {
          reject(error);
      });
    });
  },

  //--------------------------------------------------------------------------------------
  //
  // Basket Information
  //
  // -------------------------------------------------------------------------------------

  getBillingSchemes(bid: string): Promise<any> {
    return new Promise((resolve, reject) => {
      let url = '/olo/baskets/'+bid+'/billingschemes';
      PiadaOneApi.get(
        url,
        {
          headers: {
            'X-User-Verify': 'header'
          }
        },
      ).then((response) => {
        let schemes = [] as BillingScheme[];

        if (response.status === 200 && response.data && response.data.billingschemes) {
          schemes = response.data.billingschemes as BillingScheme[];
          resolve(schemes);
        } else {        
          reject(new Error('ERROR'));
        }
      }).catch((error: Error) => {
          reject(error);
      });
    });
  },

  //--------------------------------------------------------------------------------------
  //
  // Basket Times
  //
  // -------------------------------------------------------------------------------------

  getAvailableTimes(bid: string, start?: DateTime): Promise<any> {
    return new Promise((resolve, reject) => {
      let url = '/baskets/'+bid+'/availabletimes';
      if (start) {
        url += '?starttime='+(encodeURI(start.toFormat('yyyyMMdd HH:mm')));
      }

      PiadaOneApi.get(
        url,
        {
          headers: {
            'X-User-Verify': 'url'
          }
        },
      ).then((response) => {
        let times = [] as AvailableTime[];

        if (response.status === 200 && response.data && response.data.times) {
          times = response.data.times as AvailableTime[];
          resolve(times);
        } else {        
          reject(new Error('ERROR'));
        }
      }).catch((error: Error) => {
          reject(error);
      });
    });
  },
  setTimeWanted(bid: string, date: DateTime): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = '/olo/baskets/'+bid+'/timewanted';
      const body = {
        "ismanualfire": false,
        "year": date.toFormat('yyyy'),
        "month": date.toFormat('M'),
        "day": date.toFormat('d'),
        "hour": date.toFormat('H'),
        "minute": date.toFormat('m')
      };
      PiadaOneApi.put(
        url,
        body,
        {
          headers: {
            'X-User-Verify': 'body'
          }
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data && response.data.earliestreadytime) {
          basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })
          resolve(basket);
        } else if (response && response.data && response.data.message) {
          reject(new Error(response.data.message));
        } else {
          reject(new Error('Time wanted could not be set. Please try a time further in the future.'));
        }
      }).catch((error: any) => {
        if (axios.isAxiosError(error)) {
          // Do something with this error...
          if (error.response && error.response.data && error.response.data.message) {
            reject(new Error(error.response.data.message));
          } else if (error.response && error.response.data && typeof(error.response.data) == "string") {
            reject(new Error(error.response.data))
          } else {
            reject(error);
          }
        } else {
          reject(error);
        }
      });
    });
  },

  //--------------------------------------------------------------------------------------
  //
  // Basket Rewards
  //
  // -------------------------------------------------------------------------------------
  
  applyReward(bid: string, rewardcode: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = '/baskets/'+bid+'/reward';
      const body = {
        rewardcode: rewardcode,
      };

      PiadaOneApi.put(
        url,
        body,
        {
          headers: {
            'X-User-Verify': 'body'
          }
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data) {
          basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })
          resolve(basket);
        } else {
          reject(new Error('Error applying reward. Please try again later.'));
        }
      }).catch((error: Error) => {
          reject(error);
      });
    });
  },
  removeReward(bid: string, rewardcode: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = '/baskets/'+bid+'/reward?rewardcode='+rewardcode;
      PiadaOneApi.delete(
        url,
        {
          headers: {
            'X-User-Verify': 'body'
          }
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data) {
          basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })
          
          resolve(basket);
        } else {
          reject(new Error('Manual ERROR'));
        }
      }).catch((error: Error) => {
          reject(error);
      });
    });
  },

  //--------------------------------------------------------------------------------------
  //
  // Update Basket Attributes
  //
  // -------------------------------------------------------------------------------------

  applyPromo(bid: string, couponcode: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = '/baskets/'+bid+'/coupon';
      const body = {
        couponcode: couponcode,
      };
      PiadaOneApi.put(
        url,
        body,
        {
          headers: {
            'X-User-Verify': 'body'
          }
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data && response.data.id) {
          basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })
          resolve(basket);
        } else if (response.status === 200 && response.data && response.data.message) {
          reject(new Error(response.data.message));
        } else {
          reject(new Error('Promo code could not be applied.'));
        }
      }).catch((error: Error) => {
        if (axios.isAxiosError(error)) {
          // Do something with this error...
          if (error.response && error.response.data && error.response.data.message) {
            reject(new Error(error.response.data.message));
          } else if (error.response && error.response.data && typeof(error.response.data) == "string") {
            reject(new Error(error.response.data))
          } else {
            reject(error);
          }
        } else {
          reject(error);
        }
      });
    });
  },
  removePromo(bid: string, couponcode: string | null): Promise<any> {
    return new Promise((resolve, reject) => {
      let url = '/baskets/'+bid+'/coupon';

      if (couponcode != null) {
        url += '?couponcode='+couponcode;
      }
      PiadaOneApi.delete(
        url,
        {
          headers: {
            'X-User-Verify': 'body'
          }
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data) {
          basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })
          resolve(basket);
        } else {
          reject(new Error('Manual Error'));
        }
      }).catch((error: Error) => {
        if (axios.isAxiosError(error)) {
          // Do something with this error...
          if (error.response && error.response.data && error.response.data.message) {
            reject(new Error(error.response.data.message));
          } else if (error.response && error.response.data && typeof(error.response.data) == "string") {
            reject(new Error(error.response.data))
          } else {
            reject(error);
          }
        } else {
          reject(error);
        }
      });
    });
  },

  //--------------------------------------------------------------------------------------
  //
  // Basket Handoff Mode
  //
  // -------------------------------------------------------------------------------------

  applyDelivery(bid: string, streetaddress: string, city: string, building: string, zipcode: string, specialinstructions: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = '/olo/baskets/'+bid+'/dispatchaddress';
      const body = {
        streetaddress: streetaddress,
        city: city,
        building: building,
        zipcode: zipcode,
        specialinstructions: specialinstructions
      };
      PiadaOneApi.put(
        url,
        body,
        {
          headers: {
            'X-User-Verify': 'body'
          }
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data) {
          basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })
          resolve(basket);
        } else if (response && response.data) {
          if (response.data.message) {
            reject(new Error(response.data.message));
          }
        } else {
          reject(new Error('Delivery address could not be set. Please confirm all information has been set correctly.'));
        }
      }).catch((error: Error) => {
        if (axios.isAxiosError(error)) {
          // Do something with this error...
          if (error.response && error.response.data && error.response.data.message) {
            reject(new Error(error.response.data.message));
          } else if (error.response && error.response.data && typeof(error.response.data) == "string") {
            reject(new Error(error.response.data))
          } else {
            reject(error);
          }
        } else {
          reject(error);
        }
      });
    });
  },
  setTip(bid: string, amount: number): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = '/olo/baskets/'+bid+'/tip';
      const body = {
        amount: amount,
      };
      PiadaOneApi.put(
        url,
        body,
        {
          headers: {
            'X-User-Verify': 'body'
          }
        },
      ).then((response) => {
        let basket = null as (Basket | null);
        if (response.status === 200 && response.data) {
          basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })
          resolve(basket);
        } else if (response && response.data) {
          if (response.data.message) {
            reject(new Error(response.data.message));
          }
        } else {
          reject(new Error('Tip could not be set. Please try again.'));
        }
      }).catch((error: Error) => {
        if (axios.isAxiosError(error)) {
          // Do something with this error...
          if (error.response && error.response.data && error.response.data.message) {
            reject(new Error(error.response.data.message));
          } else if (error.response && error.response.data && typeof(error.response.data) == "string") {
            reject(new Error(error.response.data))
          } else {
            reject(error);
          }
        } else {
          reject(error);
        }
      });
    });
  },

  //--------------------------------------------------------------------------------------
  //
  // Basket Validate and Submit
  //
  // -------------------------------------------------------------------------------------

  validateBasket(bid: string): Promise<any> {
    return new Promise((resolve, reject) => {
      PiadaOneApi.post(
        '/olo/baskets/'+bid+'/validate',
        {
          headers: {
            'X-User-Verify': 'body'
          }
        },
      ).then((response) => {
        // let basket = null as (Basket | null);
        let basket = null as (Basket | null)
        if (response.status === 200 && response.data && response.data.id) {
          basket = plainToClass(Basket, (response.data as object), { excludeExtraneousValues: true })
          
          resolve(basket);
        } else if (response) {
          let needsTimeAdjusted = false;
          if (typeof(response.data.code != undefined) && response.data.code != null && (response.data.code == 206 || response.data.code == 218)) {
            needsTimeAdjusted = true;
          } else if (response.data != null && typeof(response.data) == 'string') {
            needsTimeAdjusted = (response.data.indexOf("specify the time you'd like this order to be ready") > -1 || response.data.indexOf("order cannot be available for delivery before") > -1 || response.data.indexOf("order cannot be ready by") > -1);
          } else if (response.data != null && typeof(response.data) == 'object' && typeof(response.data.message) == 'string') {
            needsTimeAdjusted = (response.data.message.indexOf("specify the time you'd like this order to be ready") > -1 || response.data.message.indexOf("order cannot be available for delivery before") > -1 || response.data.message.indexOf("order cannot be ready by") > -1);
          }
          if (needsTimeAdjusted) {
            reject(new Error('timeNeedsAdjusted'));
          } else {
            reject(new Error('Error Validating basket ' + response.data));
          }
        } else {
          reject(new Error('Error Validating basket'));
        }
      }).catch((error: any) => {
        if (axios.isAxiosError(error)) {
          let needsTimeAdjusted = false;

          if (error.response) {
            if (error.response.data && typeof(error.response.data.code != undefined) && error.response.data.code != null && (error.response.data.code == 206 || error.response.data.code == 218)) {
              needsTimeAdjusted = true;
            } else if (error.response.data != null && typeof(error.response.data) == 'string') {
              needsTimeAdjusted = (error.response.data.indexOf("specify the time you'd like this order to be ready") > -1 || error.response.data.indexOf("order cannot be available for delivery before") > -1 || error.response.data.indexOf("order cannot be ready by") > -1);
            } else if (error.response.data != null && typeof(error.response.data) == 'object' && typeof(error.response.data.message) == 'string') {
              needsTimeAdjusted = (error.response.data.message.indexOf("specify the time you'd like this order to be ready") > -1 || error.response.data.message.indexOf("order cannot be available for delivery before") > -1 || error.response.data.message.indexOf("order cannot be ready by") > -1);
            }
          }

          if (needsTimeAdjusted) {
            reject(new Error('timeNeedsAdjusted'));
          } else {
            // Do something with this error...
            if (error.response && error.response.data && error.response.data.message) {
              reject(new Error(error.response.data.message));
            } else if (error.response && error.response.data && typeof(error.response.data) == "string") {
              reject(new Error(error.response.data))
            } else {
              reject(error);
            }
          }
        } else {
          reject(error);
        }
      });
    });
  },

  // Submit order!
  submitOrder(bid: string, order: Order): Promise<any> {
    const body = JSON.stringify(order);

    return new Promise((resolve, reject) => {
      PiadaOneApi.post(
        '/olo/baskets/'+bid+'/submit/multiplepayments',
        body,
        {
          headers: {
            'X-User-Verify': 'body'
          }
          // No need for custom headers here
        },
      ).then((response) => {
        // let basket = null as (Basket | null);
        let order = null as (OrderDetails | null)
        if (response.status === 200 && response.data && response.data.id) {
          order = plainToClass(OrderDetails, (response.data as object), { excludeExtraneousValues: true })
          
          resolve(order);
        } else {
          if (typeof(response.data) == "string") {
            // Just return it
            reject(new Error(response.data))
          } else if(response.data && response.data.message) {
            reject(new Error(response.data.message))
          } else if(response.data && response.data.error) {
            reject(new Error(response.data.error))
          } else {
            reject(new Error('Your order could not be submitted. Please confirm all required information has been provided.'));
          }
        }
      }).catch((error: Error) => {
        if (axios.isAxiosError(error)) {
          // Do something with this error...
          if (error.response && error.response.data && error.response.data.message) {
            reject(new Error(error.response.data.message));
          } else if (error.response && error.response.data && typeof(error.response.data) == "string") {
            reject(new Error(error.response.data))
          } else {
            reject(error);
          }
        } else {
          reject(error);
        }
      });
    });
  },
};
